import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderedSet, Seq, Set } from 'immutable';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  CardStatus,
  NotificationStatus,
  UnitStatus,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { selectNotificationsByRegionId } from 'src/store/entityStores/notification-selectors';
import { selectUnitSummariesByRegionId } from 'src/store/entityStores/unit-selectors';
import { selectCardSummaryEntitiesByRegionId } from 'src/store/generated/card-summary-record.generated';
import { RegionEntityFetchService } from 'src/store/generated/client-sync.generated';
import { RegionRecord, RegionViewModelBase } from 'src/store/generated/region-record.generated';
import { createObservableLookup, ObservableLookup } from 'src/util/lookup';
import { undefinedOrMap } from 'src/util/rxjs/operators';
import { getOrElse } from 'src/util/values';

export class RegionViewModel extends RegionViewModelBase {
  public cardNumbers$: Observable<Set<string>>;
  public cardIdsByStatus: ObservableLookup<typeof CardStatus, Set<string>>;
  public nonHiddenUnitIdsByStatus: ObservableLookup<typeof UnitStatus, Seq.Indexed<string>>;
  public allUnitIdsByStatus: ObservableLookup<typeof UnitStatus, Seq.Indexed<string>>;
  public allUnitIds$: Observable<Set<string>>;
  public nonHiddenUnitIds$: Observable<Set<string>>;
  public notificationIds$: Observable<OrderedSet<string>>;
  constructor(region: RegionRecord, store: Store<AppState>) {
    super(region);
    this.cardNumbers$ = store.select(selectCardSummaryEntitiesByRegionId(this.id)).pipe(
      distinctUntilChanged(),
      map((cards) => cards.map((card) => card.cardNumber).toSet()),
      distinctUntilChanged(),
    );
    this.cardIdsByStatus = createObservableLookup(CardStatus, (cardStatus) =>
      store.select(selectCardSummaryEntitiesByRegionId(this.id)).pipe(
        distinctUntilChanged(),
        map((cards) =>
          cards
            .filter((r) => r.status === cardStatus)
            .sort((a, b) => {
              return a.dateCreated < b.dateCreated ? -1 : 1;
            })
            .map((c) => c.id)
            .toSet(),
        ),
        distinctUntilChanged(),
      ),
    );

    this.allUnitIds$ = store.select(selectUnitSummariesByRegionId(region.id)).pipe(
      distinctUntilChanged(),
      map((units) => units.map((u) => u.id).toSet()),
      distinctUntilChanged(),
    );
    this.nonHiddenUnitIds$ = store.select(selectUnitSummariesByRegionId(region.id, false)).pipe(
      distinctUntilChanged(),

      map((units) =>
        units
          .filter((u) => getOrElse(u.isHidden, false) === false)
          .map((u) => u.id)
          .toSet(),
      ),
      distinctUntilChanged(),
    );

    this.nonHiddenUnitIdsByStatus = createObservableLookup(UnitStatus, (unitStatus) =>
      store.select(selectUnitSummariesByRegionId(region.id, false)).pipe(
        distinctUntilChanged(),
        map((units) =>
          units
            .filter((u) => !getOrElse(u.isHidden, false))
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
            .filter((u) => u.status === unitStatus)
            .map((u) => u.id),
        ),
        distinctUntilChanged(),
      ),
    );

    this.allUnitIdsByStatus = createObservableLookup(UnitStatus, (unitStatus) =>
      store.select(selectUnitSummariesByRegionId(region.id)).pipe(
        distinctUntilChanged(),
        map((units) =>
          units
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
            .filter((u) => u.status === unitStatus)
            .map((u) => u.id),
        ),
        distinctUntilChanged(),
      ),
    );

    this.notificationIds$ = store
      .select(
        selectNotificationsByRegionId(
          region.id,
          NotificationStatus.AwaitingApproval,
          NotificationStatus.Failed,
        ),
      )
      .pipe(
        distinctUntilChanged(),
        map((notifications) => notifications.map((u) => u.id).toOrderedSet()),
        distinctUntilChanged(),
      );
  }
}

@Injectable({ providedIn: 'root' })
export class RegionViewModelService {
  constructor(
    private readonly _regionFetchService: RegionEntityFetchService,
    private _store: Store<AppState>,
  ) {}

  public getById(id: string): Observable<RegionViewModel | undefined> {
    return this._regionFetchService
      .getById(id)
      .pipe(undefinedOrMap((region) => new RegionViewModel(region, this._store)));
  }
}
